import { defineComponent as _defineComponent } from 'vue'
import { renderSlot as _renderSlot, unref as _unref, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, createVNode as _createVNode, normalizeClass as _normalizeClass, withCtx as _withCtx, createBlock as _createBlock, vModelText as _vModelText, withDirectives as _withDirectives, renderList as _renderList, Fragment as _Fragment, Transition as _Transition } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "ml-0.5"
}
const _hoisted_2 = { class: "mb-4 relative xl:w-1/2 2xl:mr-3" }
const _hoisted_3 = ["placeholder"]
const _hoisted_4 = { class: "" }
const _hoisted_5 = { class: "flex px-3 items-center justify-between sticky top-0 bg-white z-50" }
const _hoisted_6 = { class: "font-bold py-1" }
const _hoisted_7 = ["onClick"]
const _hoisted_8 = ["onClick"]
const _hoisted_9 = {
  key: 0,
  class: "h-4 w-4 rounded bg-yellow-600 text-white ring-1 ring-yellow cursor-pointer text-center leading-4 text-sm"
}
const _hoisted_10 = ["id", "checked"]
const _hoisted_11 = ["for", "onClick"]
const _hoisted_12 = ["onClick"]
const _hoisted_13 = ["id", "value", "checked"]
const _hoisted_14 = ["for", "onClick"]

import { Popover, PopoverButton, PopoverPanel } from "@headlessui/vue";
import { XMarkIcon } from "@heroicons/vue/20/solid";
import { ChevronUpDownIcon } from "@heroicons/vue/24/outline";
import { ref, computed, PropType, watchEffect, watch, Ref } from "vue";
import { useI18n } from "vue-i18n";
import MainButton from "@/components/UI/MainButton.vue";
import { LocationTypeFilter } from "@/types/Process";


export default /*#__PURE__*/_defineComponent({
  __name: 'Location',
  props: {
  groups: {
    type: Array as PropType<
      { name: string; field: string; options: { value: string; name: string }[] }[]
    >,
    required: true,
  },
  selected: {
    type: Object as PropType<Record<string, string[]>>,
    default: () => ({}),
  },
  placeholder: {
    type: String as PropType<string>,
    default: "",
  },
  changeOnSubmit: {
    type: Boolean,
    default: false,
  },
  defaultOpen: {
    type: Boolean,
    default: false,
  },
  tableWidth: {
    type: Number,
  },
  popoverClass: {
    type: String,
  },
  gridBoxClass: {
    type: String,
  },
},
  emits: ["update:selected"],
  setup(__props, { emit: __emit }) {

const mapDefaultSelectedProps = (value: Record<string, string[]>): Record<string, Set<string>> => {
  const result: Record<string, Set<string>> = {};
  for (const key in value) {
    result[key] = new Set(value[key]);
  }
  return result;
};

const { t } = useI18n();
const props = __props;
const selectedItems = ref();
const searchValue = ref("");
const filteredGroups = ref([{ name: "", field: "", options: [{ value: "", name: "" }] }]) as Ref<
  LocationTypeFilter[]
>;

const searchLocation = ref<HTMLElement | null>(null);

watchEffect(() => {
  if (props) {
    selectedItems.value = mapDefaultSelectedProps(props.selected);

    if (searchValue.value) {
      filteredGroups.value = props.groups.map((group) => {
        const filteredOptions = group.options.filter((option) =>
          option.value.toLowerCase().includes(searchValue.value.toLowerCase()),
        );
        return { ...group, options: filteredOptions };
      });
    } else {
      filteredGroups.value = props.groups;
    }
  }
});

const checkAll = computed<Record<string, boolean>>(() => {
  const result: Record<string, boolean> = {};
  for (const key in selectedItems.value) {
    const filteredOptions =
      filteredGroups.value.find((group) => group.field === key)?.options ?? [];
    result[key] =
      filteredOptions.length > 0 &&
      filteredOptions.every((option) => selectedItems.value[key].has(option.value));
  }
  return result;
});

const resetSearchBar = () => {
  searchValue.value = "";
};

const emits = __emit;

const groupHasSelectedValues = (group: {
  name: string;
  field: string;
  options: { value: string; name: string }[];
}) => {
  return filteredGroups.value
    ?.find((g) => g.field === group.field)
    ?.options.some((option) => selectedItems.value[group.field].has(option.value));
};

const handleAllOptionsChange = (group: string, checked: boolean) => {
  const filteredGroup: LocationTypeFilter | undefined = filteredGroups.value.find(
    (g) => g.field === group,
  );

  if (!filteredGroup) return;

  const selectedGroup = selectedItems.value[group];

  const filteredCheckedOptions = filteredGroup.options
    .filter((option) => selectedGroup.has(option.value))
    .map((option) => option.value);
  if (!checked) {
    filteredCheckedOptions.forEach((option) => selectedGroup.delete(option));
  } else {
    filteredGroup.options.forEach((option) => selectedGroup.add(option.value));
  }

  if (props.changeOnSubmit) {
    return;
  }

  const selects: Record<string, string[]> = {};
  for (const key in selectedItems.value) {
    selects[key] = Array.from(selectedItems.value[key]);
    const groupOptions = filteredGroups.value.find((g) => g.field === key)?.options ?? [];
    checkAll.value[key] = groupOptions.length === selects[key].length;
  }

  emits("update:selected", selects);
};

const handleOptionChange = (group: string, value: string) => {
  const selectedValue = selectedItems.value;
  if (selectedValue[group].has(value)) {
    selectedValue[group].delete(value);
  } else {
    selectedValue[group].add(value);
  }

  if (props.changeOnSubmit) {
    return;
  }

  const selects: Record<string, string[]> = {};
  for (const key in selectedValue) {
    selects[key] = Array.from(selectedValue[key]);
    checkAll.value[key] =
      filteredGroups.value.find((g) => g.field === key)?.options.length === selects[key].length;
  }
  emits("update:selected", selects);
};

const clearSelectedInGroup = (groupField: string) => {
  const selectedValue = selectedItems.value;
  selectedValue[groupField] = new Set();

  if (props.changeOnSubmit) {
    return;
  }

  const selects: Record<string, string[]> = {};
  for (const key in selectedValue) {
    selects[key] = Array.from(selectedValue[key]);
  }

  emits("update:selected", selectedValue);
};

const handleChange = (close: () => void) => {
  const selects: Record<string, string[]> = {};
  for (const key in selectedItems.value) {
    selects[key] = Array.from(selectedItems.value[key]);
  }
  emits("update:selected", selects);
  close();
};

const selectedCount = computed(() => {
  let count = 0;
  for (const key in selectedItems.value) {
    count += selectedItems.value[key].size;
  }
  return count;
});

watch(searchValue, (searchString) => {
  if (searchString) {
    filteredGroups.value = props.groups.map((group) => {
      const filteredOptions = group.options.filter((option) =>
        option.value.toLowerCase().includes(searchString.toLowerCase()),
      );
      checkAll.value[group.field] =
        filteredOptions.length === selectedItems.value[group.field].length;
      return { ...group, options: filteredOptions };
    });
  } else {
    filteredGroups.value = props.groups;
  }
});

const popover = ref();
watch(popover, (isOpen) => {
  if (!isOpen) {
    resetSearchBar();
  } else {
    searchLocation.value?.focus();
  }
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(Popover), { class: "relative" }, {
    default: _withCtx(({ open }) => [
      (!__props.defaultOpen)
        ? (_openBlock(), _createBlock(_unref(PopoverButton), {
            key: 0,
            class: "w-full outline-none"
          }, {
            default: _withCtx(() => [
              _renderSlot(_ctx.$slots, "default", {}, () => [
                _createElementVNode("template", {
                  class: _normalizeClass([
            'w-full group inline-flex items-center justify-between py-1.5 px-3 bg-white rounded-md text-left shadow-sm ring-1 ring-inset ring-gray-300 focus:outline-none focus:ring-2 focus:ring-yellow-500 sm:text-sm text-xs sm:leading-6  text-gray-400',
            open ? 'ring-2 ring-yellow-500' : '',
          ])
                }, [
                  _createElementVNode("span", null, [
                    _createTextVNode(_toDisplayString(_unref(t)(__props.placeholder)), 1),
                    (selectedCount.value)
                      ? (_openBlock(), _createElementBlock("span", _hoisted_1, "(" + _toDisplayString(selectedCount.value) + ")", 1))
                      : _createCommentVNode("", true)
                  ]),
                  _createVNode(_unref(ChevronUpDownIcon), {
                    class: "ml-2 h-5 w-5 transition duration-150 ease-in-out",
                    "aria-hidden": "true"
                  })
                ], 2)
              ])
            ]),
            _: 2
          }, 1024))
        : _createCommentVNode("", true),
      _createVNode(_Transition, {
        "enter-active-class": "transition duration-200 ease-out",
        "enter-from-class": "translate-y-1 opacity-0",
        s: "",
        "enter-to-class": "translate-y-0 opacity-100",
        "leave-active-class": "transition duration-150 ease-in",
        "leave-from-class": "translate-y-0 opacity-100",
        "leave-to-class": "translate-y-1 opacity-0"
      }, {
        default: _withCtx(() => [
          _createVNode(_unref(PopoverPanel), {
            focus: "",
            class: _normalizeClass(["absolute z-50 min-w-max h-max w-full sm:px-0 lg:max-w-5xl max-h-screen overflow-y-scroll xl:overflow-y-hidden", [
          __props.defaultOpen
            ? '-left-full md:right-0 md:-left-1/2  2xl:-left-1/2 -translate-x-1/2'
            : 'md:right-0 lg:left-0 lg:-translate-x-1/4 lg:right-auto md:-translate-x-w-max mt-3',
          __props.popoverClass,
        ]]),
            static: __props.defaultOpen
          }, {
            default: _withCtx(({ close }) => [
              _createElementVNode("div", {
                class: "bg-gray-50 border border-gray-200 shadow rounded-lg p-4",
                ref_key: "popover",
                ref: popover
              }, [
                _createElementVNode("div", _hoisted_2, [
                  _withDirectives(_createElementVNode("input", {
                    ref_key: "searchLocation",
                    ref: searchLocation,
                    type: "text",
                    class: "w-full px-3 py-1 text-sm rounded-md border border-gray-300 focus:ring-yellow-500 focus:border-yellow-500",
                    placeholder: _unref(t)('buttons.search'),
                    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((searchValue).value = $event))
                  }, null, 8, _hoisted_3), [
                    [_vModelText, searchValue.value]
                  ]),
                  (searchValue.value !== '')
                    ? (_openBlock(), _createBlock(_unref(XMarkIcon), {
                        key: 0,
                        class: "w-4 absolute items-center bottom-0 right-1 top-1.5 text-gray-500",
                        onClick: resetSearchBar
                      }))
                    : _createCommentVNode("", true)
                ]),
                _createElementVNode("div", {
                  class: _normalizeClass([[
              {
                'grid-cols-2': (filteredGroups.value?.length ?? 0) === 2,
                'xl:grid-cols-3': (filteredGroups.value?.length ?? 0) === 3,
                'xl:grid-cols-4': (filteredGroups.value?.length ?? 0) >= 4,
              },
              __props.gridBoxClass,
            ], "sm:grid lg:grid-flow-auto grid-cols-2 gap-2 items-start mb-4"])
                }, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(filteredGroups.value, (group, idx) => {
                    return (_openBlock(), _createElementBlock("div", {
                      class: "overflow-y-auto pl-4 pr-2 lg:max-w-[280px] lg:w-[280px] shadow bg-white h-96 sm:max-w-1/2",
                      key: idx
                    }, [
                      _createElementVNode("div", _hoisted_4, [
                        _createElementVNode("div", _hoisted_5, [
                          _createElementVNode("p", _hoisted_6, _toDisplayString(group.name), 1),
                          (groupHasSelectedValues(group))
                            ? (_openBlock(), _createElementBlock("button", {
                                key: 0,
                                onClick: ($event: any) => (clearSelectedInGroup(group.field)),
                                class: "text-gray-500 text-xs hover:underline",
                                type: "button"
                              }, _toDisplayString(_ctx.$t("buttons.clear")), 9, _hoisted_7))
                            : _createCommentVNode("", true)
                        ]),
                        _createElementVNode("div", {
                          class: "py-2 px-3 rounded-md cursor-pointer w-full flex items-center hover:bg-yellow-200",
                          onClick: ($event: any) => (handleAllOptionsChange(group.field, !checkAll.value[group.field]))
                        }, [
                          (
                      groupHasSelectedValues(group) &&
                      !checkAll.value[group.field] &&
                      selectedItems.value[group.field].size > 0
                    )
                            ? (_openBlock(), _createElementBlock("div", _hoisted_9, " - "))
                            : (_openBlock(), _createElementBlock("input", {
                                key: 1,
                                type: "checkbox",
                                id: `${group.field}-all`,
                                checked: checkAll.value[group.field],
                                class: "h-4 w-4 rounded border-gray-300 text-yellow-600 focus:ring-yellow-500 cursor-pointer"
                              }, null, 8, _hoisted_10)),
                          _createElementVNode("label", {
                            for: `${group.field}-all`,
                            class: "ml-3 text-sm",
                            onClick: ($event: any) => (handleAllOptionsChange(group.field, !checkAll.value[group.field]))
                          }, [
                            _createTextVNode(_toDisplayString(_unref(t)("analytics.planner.all")) + " ", 1),
                            _createElementVNode("span", null, "(" + _toDisplayString(group.options.length) + ")", 1)
                          ], 8, _hoisted_11)
                        ], 8, _hoisted_8),
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(group.options, (option, idx) => {
                          return (_openBlock(), _createElementBlock("div", {
                            key: idx,
                            class: "py-2 px-3 rounded-md cursor-pointer w-full flex items-center hover:bg-yellow-200",
                            onClick: ($event: any) => (handleOptionChange(group.field, option.value))
                          }, [
                            _createElementVNode("input", {
                              type: "checkbox",
                              id: `${group.field}-${idx}`,
                              value: option.value,
                              checked: selectedItems.value[group.field]?.has(option.value),
                              class: "h-4 w-4 rounded border-gray-300 text-yellow-600 focus:ring-yellow-600"
                            }, null, 8, _hoisted_13),
                            _createElementVNode("label", {
                              for: `${group.field}-${idx}`,
                              class: "ml-3 text-sm",
                              onClick: ($event: any) => (handleOptionChange(group.field, option.value))
                            }, _toDisplayString(option.name), 9, _hoisted_14)
                          ], 8, _hoisted_12))
                        }), 128))
                      ])
                    ]))
                  }), 128))
                ], 2),
                (__props.changeOnSubmit)
                  ? (_openBlock(), _createBlock(MainButton, {
                      key: 0,
                      label: _unref(t)('buttons.apply'),
                      class: "sm:mr-0 !w-full",
                      type: "button",
                      color: "yellow",
                      onClick: ($event: any) => (handleChange(close))
                    }, null, 8, ["label", "onClick"]))
                  : _createCommentVNode("", true)
              ], 512)
            ]),
            _: 1
          }, 8, ["class", "static"])
        ]),
        _: 1
      })
    ]),
    _: 3
  }))
}
}

})